import { Check, ImageIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { EmptyState } from '@nucleus-commerce/admin-kit/layout';
import type { Media } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { formatBytes } from './format-bytes';
import { MediaThumb } from './media-thumb';

export interface MediaGridProps {
  items: readonly Media[] | undefined;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  onSelect: (media: Media) => void;
  /** Ids shown with a check mark (picker mode). */
  selectedIds?: ReadonlySet<string>;
  /** Ids shown but not pickable, marked with `lockedLabel` (picker mode). */
  lockedIds?: ReadonlySet<string>;
  lockedLabel?: string;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
  emptyAction?: ReactNode;
  className?: string;
  /** Replaces the column classes, for a grid narrower than the page (the picker dialog). */
  columnsClassName?: string;
  /** The library page's grid, which the media tour points at. */
  tour?: boolean;
}

/** Square tiles with the uploaded file name (or the alt text), dimensions and size under each. */
export function MediaGrid({
  items,
  isLoading = false,
  error,
  onRetry,
  onSelect,
  selectedIds,
  lockedIds,
  lockedLabel,
  hasMore = false,
  isFetchingMore = false,
  onLoadMore,
  emptyAction,
  className,
  columnsClassName = 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6',
  tour = false,
}: MediaGridProps) {
  const { t } = useTranslation('media');

  if (error !== undefined && error !== null) {
    return <ErrorState error={error} onRetry={onRetry} />;
  }
  if (!isLoading && items?.length === 0) {
    return (
      <EmptyState
        icon={ImageIcon}
        title={t('empty.title')}
        description={t('empty.description')}
        action={emptyAction}
        tour={tour ? 'media.library.empty' : undefined}
        tourLink={tour}
      />
    );
  }
  return (
    <div
      className={cn('flex flex-col gap-4', className)}
      data-tour={tour ? 'media.library.grid' : undefined}
    >
      <ul className={cn('grid gap-3', columnsClassName)}>
        {isLoading
          ? Array.from({ length: 12 }).map((_, i) => (
              <li key={i}>
                <Skeleton className="aspect-square w-full rounded-md" />
              </li>
            ))
          : items?.map((media) => {
              const selected = selectedIds?.has(media.id) ?? false;
              const locked = lockedIds?.has(media.id) ?? false;
              return (
                <li key={media.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(media)}
                    disabled={locked}
                    aria-pressed={selectedIds ? selected : undefined}
                    className={cn(
                      'group focus-visible:ring-ring/50 relative block w-full rounded-lg text-left outline-none focus-visible:ring-[3px]',
                      locked && 'cursor-default',
                    )}
                  >
                    <MediaThumb
                      media={media}
                      className={cn(
                        'w-full border transition-colors',
                        locked ? 'opacity-60' : 'group-hover:border-primary/60',
                        selected && 'ring-primary ring-2',
                      )}
                    />
                    {selected ? (
                      <span className="bg-primary text-primary-foreground absolute top-2 right-2 flex size-5 items-center justify-center rounded-full">
                        <Check className="size-3" aria-hidden />
                      </span>
                    ) : null}
                    <span
                      className="mt-1.5 block truncate text-xs font-medium"
                      title={media.filename ?? undefined}
                    >
                      {media.filename ?? media.alt ?? t('untitled')}
                    </span>
                    <span className="text-muted-foreground block text-[11px] tabular-nums">
                      {media.width && media.height ? `${media.width}×${media.height} · ` : ''}
                      {formatBytes(media.bytes)}
                    </span>
                    {locked ? (
                      <span className="text-muted-foreground mt-1 inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium">
                        {lockedLabel}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
      </ul>
      {hasMore && onLoadMore ? (
        <div className="flex justify-center">
          <Button variant="outline" onClick={onLoadMore} disabled={isFetchingMore}>
            {isFetchingMore ? t('loadingMore') : t('loadMore')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
